/**
 * Shared helpers for rendering customer reviews (product detail + reviews page).
 * Keeps display logic — how a reviewer is named, photo extraction, variant
 * labelling —
 * in one place so the inline section and the full reviews page stay in sync.
 */

/**
 * THE ONE FUNCTION THAT TURNS A CUSTOMER INTO A VISIBLE NAME.
 *
 * It used to mask — `محمد عبد الرحمن` → `M***d` — and the owner has reversed
 * that decision: "show the name of the reviewer, the first name". First name
 * only is the compromise retail settles on, and the masked form was telling a
 * shopper nothing while looking like a redaction on the one page whose job is
 * to feel trustworthy.
 *
 * IT STAYS ONE FUNCTION, and that is the whole safety argument. A reviewer's
 * row carries a name the customer never chose to publish, so the amount of it
 * that reaches the page has to be decided in one place and read from four —
 * the product card, the product reviews modal, the business reviews section
 * and its modal. `reviewerNameIsTheFirstName.test.js` enumerates the callers
 * from the sources and fails if a fifth surface names a customer any other
 * way, and asserts the surname, the phone, the email and the photo do not
 * travel with it.
 *
 * THE FIRST NAME IS THE FIRST WORD, and "word" has to mean what it means in
 * the scripts this shop runs. Splitting on `/\s+/` alone misses U+00A0, which
 * is exactly what a name typed on a phone keyboard arrives with — the whole
 * name would then be "the first word" and the surname would ship. Unicode
 * whitespace is normalised first, so `محمد عبد الرحمن` and `יוסי כהן` give
 * `محمد` and `יוסי` rather than themselves.
 *
 * A dash for nothing at all: a caller must never be able to paint a blank
 * where a person's name goes, and must never invent one.
 */
export function reviewerDisplayName(name) {
  const normalised = (name ?? '')
    .toString()
    // Every Unicode space separator, plus the ones outside `\s` that a phone
    // keyboard still produces — U+00A0 above all, which is what turns a whole
    // name into "the first word" and ships the surname. Written as escapes:
    // U+2028/U+2029 are line terminators, and a literal one ends the source
    // line it is written on.
    .replace(/[\s\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/g, ' ')
    .trim();
  if (!normalised) return '—';
  return normalised.split(' ')[0];
}

/**
 * Normalise a review's photos into a flat array of URLs. Supports both the
 * newer `imageUrls` array and the legacy single `imageUrl` so old reviews keep
 * rendering after the multi-photo rollout.
 */
export function reviewPhotos(review) {
  if (!review) return [];
  const many = Array.isArray(review.imageUrls) ? review.imageUrls.filter(Boolean) : [];
  if (many.length) return many;
  return review.imageUrl ? [review.imageUrl] : [];
}

/** Human-readable variant label from the snapshot, e.g. "Black · M · Slim". */
export function variantLabel(review) {
  const attrs = review?.variantAttrsSnapshot ?? {};
  return [attrs.color, attrs.size, attrs.style].filter(Boolean).join(' · ');
}

/**
 * Service name an appointment review is for — used as the by-service filter
 * axis on a salon's public ratings. Tolerates a few server shapes: the actual
 * API nests it under `serviceSnapshot.serviceName`; older/flat shapes (a flat
 * snapshot field or a nested service object) are kept as fallbacks.
 */
export function serviceLabel(review) {
  return (
    review?.serviceSnapshot?.serviceName
    ?? review?.serviceNameSnapshot
    ?? review?.serviceName
    ?? review?.service?.name
    ?? ''
  ).toString().trim();
}

/**
 * Specialist (team member) name an appointment review is for — used as the
 * per-specialist filter axis. Same tolerance as serviceLabel; the live API
 * nests it under `serviceSnapshot.specialistName`.
 */
export function specialistLabel(review) {
  return (
    review?.serviceSnapshot?.specialistName
    ?? review?.specialistNameSnapshot
    ?? review?.specialistName
    ?? review?.specialist?.displayName
    ?? review?.specialist?.name
    ?? ''
  ).toString().trim();
}

/** Stable id for a review's service (for grouping chips that share a name). */
export function serviceKey(review) {
  return review?.serviceId ?? review?.service?.id ?? null;
}

/** Stable id for a review's specialist. */
export function specialistKey(review) {
  return review?.specialistId ?? review?.specialist?.id ?? null;
}

/** Format a review date for display in the active locale (date only). */
export function formatReviewDate(value, locale) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(locale || undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch (_) {
    return d.toLocaleDateString();
  }
}
